import { useMutation, useQuery } from '@apollo/client';
import type { CreatePolicyInput, DeletePolicyInput, Policy, UpdatePolicyInput } from '@communities/content-management/domain';
import { CREATE_STRIKE_POLICY, DELETE_STRIKE_POLICY, GET_STRIKE_POLICIES, UPDATE_STRIKE_POLICY } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useMemo, useState } from 'react';

interface StrikePoliciesResponse {
  community: {
    id: string;
    policies: Policy[];
  } | null;
}

interface CreateStrikePolicyResponse {
  createPolicy: Policy;
}

interface UpdateStrikePolicyResponse {
  updatePolicy: Policy;
}

interface DeleteStrikePolicyResponse {
  deletePolicy: boolean;
}

export function useStrikePolicies(communityId?: string) {
  const [mutationErrors, setMutationErrors] = useState<DomainError[] | null>(null);

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<StrikePoliciesResponse>(GET_STRIKE_POLICIES, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
    skip: !communityId,
  });

  const refetchConfig = communityId ? [{ query: GET_STRIKE_POLICIES, variables: { communityId } }] : [];

  const [createPolicyMutation, { loading: createLoading }] = useMutation<CreateStrikePolicyResponse>(CREATE_STRIKE_POLICY, {
    refetchQueries: refetchConfig,
    awaitRefetchQueries: true,
  });

  const [updatePolicyMutation, { loading: updateLoading }] = useMutation<UpdateStrikePolicyResponse>(UPDATE_STRIKE_POLICY, {
    refetchQueries: refetchConfig,
    awaitRefetchQueries: true,
  });

  const [deletePolicyMutation, { loading: deleteLoading }] = useMutation<DeleteStrikePolicyResponse>(DELETE_STRIKE_POLICY, {
    refetchQueries: refetchConfig,
    awaitRefetchQueries: true,
  });

  const queryErrors: DomainError[] | null = useMemo(() => {
    if (!queryError) return null;

    return transformErrors(queryError);
  }, [queryError]);

  const errors = mutationErrors || queryErrors;

  // Filter policies to only include strike workflow type (case-insensitive)
  const policies: Policy[] = useMemo(() => {
    const allPolicies = data?.community?.policies || [];

    return allPolicies.filter(policy => policy.workflowType && policy.workflowType.toLowerCase() === 'strike');
  }, [data?.community?.policies]);

  const resetErrors = useCallback(() => {
    setMutationErrors(null);
  }, []);

  const createPolicy = async (communityId: string, input: CreatePolicyInput): Promise<boolean> => {
    try {
      track('CreateStrikePolicyInitiated', {
        communityId,
        policyName: input.name,
      });

      setMutationErrors(null);
      await createPolicyMutation({
        variables: {
          communityId,
          input,
        },
      });

      track('CreateStrikePolicySucceeded', {
        communityId,
        policyName: input.name,
      });

      return true;
    } catch (error) {
      track('CreateStrikePolicyFailed', {
        communityId,
        policyName: input.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setMutationErrors(transformErrors(error));

      return false;
    }
  };

  const updatePolicy = async (communityId: string, policyId: string, input: UpdatePolicyInput): Promise<boolean> => {
    try {
      track('UpdateStrikePolicyInitiated', {
        communityId,
        policyId,
      });

      setMutationErrors(null);
      await updatePolicyMutation({
        variables: {
          communityId,
          policyId,
          input,
        },
      });

      track('UpdateStrikePolicySucceeded', {
        communityId,
        policyId,
      });

      return true;
    } catch (error) {
      track('UpdateStrikePolicyFailed', {
        communityId,
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setMutationErrors(transformErrors(error));

      return false;
    }
  };

  const deletePolicy = async (communityId: string, policyId: string): Promise<boolean> => {
    try {
      track('DeleteStrikePolicyInitiated', {
        communityId,
        policyId,
      });

      setMutationErrors(null);
      await deletePolicyMutation({
        variables: {
          communityId,
          input: { policyId } as DeletePolicyInput,
        },
      });

      track('DeleteStrikePolicySucceeded', {
        communityId,
        policyId,
      });

      return true;
    } catch (error) {
      track('DeleteStrikePolicyFailed', {
        communityId,
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setMutationErrors(transformErrors(error));

      return false;
    }
  };

  return {
    policies,
    loading: queryLoading || createLoading || updateLoading || deleteLoading,
    errors,
    createPolicy,
    updatePolicy,
    deletePolicy,
    refetch,
    resetErrors,
  };
}
